import { useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { useColorScheme } from '@mui/material/styles';
import { ArcElement, Chart as ChartJS } from 'chart.js';
import { sum } from 'ramda';
import { Doughnut } from 'react-chartjs-2';
import { useSelector } from 'react-redux';

import { getMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components/index';
import fetch from 'features/common/fetch';
import { getContactsSummary } from 'features/views/dashboard/campaigns/selectors';

import LegendAndData from './LegendAndData';

ChartJS.register(ArcElement);

export const CampaignInfoGraphics = () => {
	const chartRef = useRef(null);
	const { mode: theme } = useColorScheme();

	const campaignContactsSummary = useSelector(getContactsSummary);
	const allContactsSum = campaignContactsSummary?.notDone + campaignContactsSummary?.progress;

	const generateData = useMemo(() => {
		if (allContactsSum > 0) {
			// used when we receive some actual data, not zeros
			const orderedData = [campaignContactsSummary?.notDone, campaignContactsSummary?.progress];

			return {
				datasets: [
					{
						data: orderedData,
						backgroundColor: ['#EF6752', '#F6A363'],
						borderRadius: 10,
						borderWidth: 3,
						borderColor: '#F5F5F5',
					},
				],
			};
		} else {
			return {
				// used when we receive zeros
				// just placeholder data, to create "disabled" feel > graph with one full value and ligthGrey color
				datasets: [
					{
						data: [100],
						backgroundColor: ['lightGrey'],
						borderRadius: 10,
						borderWidth: 3,
						borderColor: '#F5F5F5',
					},
				],
			};
		}
	}, [allContactsSum, campaignContactsSummary?.notDone, campaignContactsSummary?.progress]);

	const options = {
		cutout: '75%',
		maintainAspectRatio: false,
		hover: { mode: null },
		plugins: {
			tooltip: {
				enabled: false,
			},
		},
	};

	const generatePlugins = useMemo(
		() => [
			{
				id: 'campaignsContactsSummaryChart',
				beforeDraw(chart) {
					const data = chart?.data?.datasets[0]?.data;
					const { width } = chart;
					const { height } = chart;
					const { ctx } = chart;
					ctx.restore();

					ctx.font = `${(height / 4).toFixed(2)}px sans-serif`;
					const textCount = data.length === 2 ? sum(data) : 0;
					ctx.textAlign = 'center';
					ctx.fillStyle = theme === 'light' ? 'black' : 'white';
					ctx.fillText(textCount, width / 2, height / 2 - 5);
					ctx.restore();

					ctx.font = `${(height / 10).toFixed(2)}px sans-serif`;
					ctx.textAlign = 'center';
					ctx.fillText(getMessage('dashboard:campaigns.total'), width / 2, height / 2 + 12);
					ctx.restore();

					ctx.font = `${(height / 10).toFixed(2)}px sans-serif`;
					ctx.textAlign = 'center';
					ctx.fillText(getMessage('dashboard:campaigns.contacts'), width / 2, height / 2 + 25);

					ctx.save();
				},
			},
		],
		[theme],
	);

	return (
		<ComponentLoader fetchStorePath={fetch.constants.urls.campaignContactsSummary} forceInlineError>
			<Paper sx={{ p: 3, backgroundColor: (theme) => theme.palette.background[100], borderRadius: 1 }}>
				<Grid container>
					<Grid size={6}>
						<Box sx={{ height: '106px', width: '106px', mx: 3 }}>
							<Doughnut
								ref={chartRef}
								data={generateData}
								options={options}
								redraw
								plugins={generatePlugins}
							/>
						</Box>
					</Grid>
					<Grid size={6}>
						<LegendAndData contactsSummary={campaignContactsSummary} />
					</Grid>
				</Grid>
			</Paper>
		</ComponentLoader>
	);
};

export default CampaignInfoGraphics;
